import {
  createSlice,
  createAsyncThunk,
  type PayloadAction,
} from '@reduxjs/toolkit';
import type { CaseInformationResponse } from '@/types/case';
import { api } from '@/store/api';
import { setSessionCaseInfo } from '@/store/slices/callSlice';

/**
 * Track the latest case fetch per contact so that stale in-flight requests
 * cannot overwrite results from newer requests.  When the agent relinks a
 * customer or quickly closes / re-creates cases, multiple fetchCaseById
 * calls overlap — the one that finishes last would otherwise silently
 * overwrite the correct (newer) case in both the session and the case slice.
 *
 * Each fetch gets a unique sequence number so that repeated fetches for the
 * **same** case (e.g. edit → save → refetch) are also protected — the older
 * fetch's sequence will no longer match the latest one.
 */
let fetchCaseSequence = 0;
const latestCaseFetchByContact = new Map<string, number>();

/** Fetch case by id and store in Redux. Dispatched when call/chat init provides a caseId. */
export const fetchCaseById = createAsyncThunk(
  'case/fetchById',
  async (
    { caseId, contactId }: { caseId: number; contactId?: string },
    { dispatch, getState }
  ) => {
    const fetchKey = contactId ?? '__no_contact__';
    const mySeq = ++fetchCaseSequence;
    latestCaseFetchByContact.set(fetchKey, mySeq);

    const result = await dispatch(
      api.endpoints.getCaseById.initiate({ caseId }, { forceRefetch: true })
    ).unwrap();

    // Discard if a newer fetch for the same contact was started while we
    // were waiting (e.g. agent re-linked, created a different case, or
    // triggered another save-refetch of the same case).
    if (latestCaseFetchByContact.get(fetchKey) !== mySeq) {
      return { caseInfo: result, shouldUpdateFocused: false };
    }

    // Only update the session if it still exists — the contact may have been
    // removed (e.g. agent clicked Finish After Call Work) while the fetch was
    // in flight.  ensureSession would otherwise recreate a deleted session and
    // re-focus it, resurrecting stale case data and transcriptions.
    if (
      contactId &&
      (getState() as { call?: { sessions?: Record<string, unknown> } }).call
        ?.sessions?.[contactId]
    ) {
      dispatch(setSessionCaseInfo({ contactId, caseInfo: result }));
    }
    const focusedContactId = (
      getState() as { call?: { focusedContactId?: string | null } }
    ).call?.focusedContactId;
    return {
      caseInfo: result,
      shouldUpdateFocused: !contactId || focusedContactId === contactId,
    };
  }
);

interface CaseState {
  currentCase: CaseInformationResponse | null;
  isLoading: boolean;
  error: string | null;
  /** Contact whose case drawer is open with unsaved edits; the ACW Finish button warns before clearing it. */
  unsavedEditsContactId: string | null;
}

const initialState: CaseState = {
  currentCase: null,
  isLoading: false,
  error: null,
  unsavedEditsContactId: null,
};

const caseSlice = createSlice({
  name: 'case',
  initialState,
  reducers: {
    setCaseInfo: (
      state,
      action: PayloadAction<CaseInformationResponse | null>
    ) => {
      state.currentCase = action.payload;
      state.error = null;
    },
    clearCaseInfo: () => initialState,
    setUnsavedCaseEditsContact: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.unsavedEditsContactId = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCaseById.pending, (state, action) => {
        if (!action.meta.arg.contactId) {
          state.isLoading = true;
          state.error = null;
        }
      })
      .addCase(fetchCaseById.fulfilled, (state, action) => {
        if (action.payload.shouldUpdateFocused) {
          state.isLoading = false;
          state.error = null;
          state.currentCase = action.payload.caseInfo;
        }
      })
      .addCase(fetchCaseById.rejected, (state, action) => {
        if (!action.meta.arg.contactId) {
          state.isLoading = false;
          state.error = action.error.message ?? 'Failed to load case';
        }
      });
  },
});

export const { setCaseInfo, clearCaseInfo, setUnsavedCaseEditsContact } =
  caseSlice.actions;
export default caseSlice.reducer;
